import { useCurrentUser } from "client/hooks/use-current-user";
import { useEffect, useState } from "react";
import { loadCachedUser, saveCachedUser } from "../lib/auth";
import { useNetworkStatus } from "./useNetworkStatus";

type CurrentUser = NonNullable<ReturnType<typeof useCurrentUser>["data"]>;

type Result = {
  // Effective user for rendering. Offline (or while the online fetch is still
  // in flight) this falls back to the user cached from the last successful
  // fetch, so the shell opens without waiting on the network. Once the query
  // settles online, only a fresh server-confirmed user counts: a settled
  // UNAUTHORIZED yields null so the auth gate shows the login screen instead
  // of a shell whose API calls all 401 off a stale cached session.
  user: CurrentUser | null;
  // Initial current-user fetch in flight (only possible online — the query is
  // disabled while offline).
  isLoading: boolean;
  refetch: ReturnType<typeof useCurrentUser>["refetch"];
};

export function useIPhoneCurrentUser(): Result {
  const { isOnline } = useNetworkStatus();
  const query = useCurrentUser(isOnline);
  const [cachedUser, setCachedUser] = useState<CurrentUser | null>(() =>
    loadCachedUser<CurrentUser>(),
  );

  useEffect(() => {
    if (!query.data) {
      return;
    }

    saveCachedUser(query.data);
    setCachedUser(query.data);
  }, [query.data]);

  return {
    user: !isOnline || query.isLoading ? cachedUser : (query.data ?? null),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
